import Logger from '../Logging';
import { CreateReduxConnector } from './ReduxConnector';
import {
	changeContext,
	contextSignal,
	disconnectPeer,
	leaveContext,
	remoteTrackAdded
} from '../../Redux/Actions/webrtcActions';
import LocalMediaHandler from './LocalMediaHandler';
// eslint-disable-next-line no-unused-vars
import adapter from 'webrtc-adapter';

class PeerContext {
	PeerId;
	UserId;
	DispName;
	Context;
	constructor(PeerId, UserId, DispName, Context) {
		this.PeerId = PeerId;
		this.UserId = UserId;
		this.DispName = DispName;
		this.Context = Context;
	}
}

class GRTCConstants {
	static BASE_INFO = 'Utilities/Gnural/GnuralRTCv2/';
	static DEFAULT_CONFIGURATION = {
		bundlePolicy: 'balanced',
		iceCandidatePoolSize: 20,
		iceServers: [
			{ urls: 'stun:stun.l.google.com:19302' },
			{ urls: 'stun:www.livetoair.net:3478' },
			{ urls: 'stun:54.188.158.43:3478' }
		],
		iceTransportPolicy: 'all',
		rtcpMuxPolicy: 'require',
	};
	static DEFAULT_RELAY_SERVERS = [
		'turn:54.188.158.43:3444?transport=udp'
	];
	static DEFAULT_SENDER_VIDEO_PARAMETERS = {
		encodings: [
			{
				active: true,
				maxFramerate: 30
			}
		],
		degradationPreference: 'balanced'
	};
	static DEFAULT_SENDER_LOW_BANDWIDTH_VIDEO_PARAMETERS = {
		encodings: [
			{
				active: true,
				maxFramerate: 20,
				maxBitrate: 800000,
				scaleResolutionDownBy: 1.5
			}
		],
		degradationPreference: 'maintain-framerate'
	};
	static DEFAULT_SENDER_HIGH_BANDWIDTH_MIN_BANDWIDTH = 8000;
	static DEFAULT_SENDER_HIGH_BANDWIDTH_START_BANDWIDTH = 15000;
	static DEFAULT_SENDER_HIGH_BANDWIDTH_MAX_BANDWIDTH = 20000;
	static DEFAULT_SENDER_HIGH_BANDWIDTH_VIDEO_PARAMETERS = {
		encodings: [
			{
				active: true,
				maxFramerate: 30,
				maxBitrate: GRTCConstants.DEFAULT_SENDER_HIGH_BANDWIDTH_MAX_BANDWIDTH * 1000,
				priority: 'medium'
			}
		],
		degradationPreference: 'maintain-resolution'
	};
	static DEFAULT_SENDER_AUDIO_PARAMETERS = {
		encodings: [
			{
				active: true,
				channels: 2,
				priority: 'high'
			}
		],
	};
	static DEFAULT_SENDER_LOW_BANDWIDTH_AUDIO_PARAMETERS = {
		encodings: [
			{
				active: true,
				channels: 1,
				priority: 'high',
				maxBitrate: 32000
			}
		],
	};
	static AddedRelayServers = [];
	static SenderVideoParameters = { ...GRTCConstants.DEFAULT_SENDER_VIDEO_PARAMETERS };
	static SenderAudioParameters = { ...GRTCConstants.DEFAULT_SENDER_AUDIO_PARAMETERS };
	static IsHighBandwidthModeEnabled = false;
}

class GPeerConnection {

	static async Create(peerId, Context, userId, dispName) {
		let tempConnection = new GPeerConnection(peerId, Context, userId, dispName);
		await tempConnection.CreateRTCPeerConnection();
		return tempConnection;
	}
	#LOCAL_INFO = GRTCConstants.BASE_INFO + 'GPeerConnection/';
	#ReduxConnector = CreateReduxConnector();
	#PeerId;
	#UserId;
	#DispName;
	#Context;
	#RTCConfiguration;
	#RTCPeerConnection = null;
	#FetchStatsInterval = null;
	#RemoteStream = new MediaStream();
	#IsDisposed = false;
	constructor(peerId, Context, userId, dispName) {
		this.#PeerId = peerId;
		this.#UserId = userId;
		this.#DispName = dispName;
		this.#Context = Context;
		this.#RTCConfiguration = {
			...GRTCConstants.DEFAULT_CONFIGURATION,
			iceServers: [
				...GRTCConstants.DEFAULT_CONFIGURATION.iceServers,
				...(GRTCConstants.AddedRelayServers.count ?
					GRTCConstants.AddedRelayServers.map(server => {
						return {
							urls: server,
							credential: Context,
							username: Context
						};
					})
					:
					GRTCConstants.DEFAULT_RELAY_SERVERS.map(server => {
						return {
							urls: server,
							credential: Context,
							username: Context
						};
					})
				)
			]
		};
		this.#LOCAL_INFO = `{${Context}, ${peerId}, ${userId}, ${dispName}} ` + this.#LOCAL_INFO;
	}

	#OnIceConnectionStateChange() {
		if (this.#IsDisposed) return;
		Logger.debug(this.#LOCAL_INFO + 'OnIceConnectionStateChange', this.#RTCPeerConnection.iceConnectionState);
	}
	#OnIceGatheringStateChange() {
		if (this.#IsDisposed) return;
		Logger.debug(this.#LOCAL_INFO + 'OnIceGatheringStateChange', this.#RTCPeerConnection.iceGatheringState);
	}
	#OnError(data) {
		Logger.error(this.#LOCAL_INFO + 'OnError', data);
		if (this.#IsDisposed) {
			Logger.error(this.#LOCAL_INFO + 'OnError', 'Failed to Handle Error: Disposed');
			return;
		}
	}
	#OnNegotiationNeeded(data) {
		Logger.debug(this.#LOCAL_INFO + 'OnNegotiationNeeded', data);
		if (this.#IsDisposed) {
			Logger.error(this.#LOCAL_INFO + 'OnNegotiationNeeded', 'Failed to Handle Negotiation Needed: Disposed');
			return;
		}
	}
	#OnRemoveStream(data) {
		Logger.debug(this.#LOCAL_INFO + 'OnRemoveStream', data);
		if (this.#IsDisposed) {
			Logger.error(this.#LOCAL_INFO + 'OnRemoveStream', 'Failed to Handle Remove Stream: Disposed');
			return;
		}
	}
	#OnSignalingStateChange() {
		Logger.debug(this.#LOCAL_INFO + 'OnSignalingStateChange', this.#RTCPeerConnection.signalingState);
		if (this.#IsDisposed) {
			Logger.error(this.#LOCAL_INFO + 'OnSignalingStateChange', 'Failed to Handle Signaling State Change: Disposed');
			return;
		}
	}
	#OnTrack(event) {
		Logger.debug(this.#LOCAL_INFO + 'OnTrack', event);
		if (this.#IsDisposed) {
			Logger.error(this.#LOCAL_INFO + 'OnTrack', 'Failed to Handle Track: Disposed');
			return;
		}
		if (event.track && !this.#RemoteStream.getTracks().includes(event.track)) {
			this.#RemoteStream.addTrack(event.track);
		}
		Logger.debug(this.#LOCAL_INFO + 'OnTrack', 'Remote stream tracks:', this.#RemoteStream.getTracks());
		this.#ReduxConnector.dispatch(remoteTrackAdded());
	}
	#OnIceCandidate(event) {
		Logger.debug(this.#LOCAL_INFO + 'OnIceCandidate', event);
		if (this.#IsDisposed) {
			Logger.error(this.#LOCAL_INFO + 'OnIceCandidate', 'Failed to Handle Ice Candidate: Disposed');
			return;
		}
		if (event.candidate) {
			let candidate = {
				type: 'candidate',
				label: event.candidate.sdpMLineIndex,
				id: event.candidate.sdpMid,
				candidate: event.candidate.candidate
			};
			this.#ReduxConnector.dispatch(contextSignal(this.#Context.PeerId, null, this.#PeerId, JSON.stringify(candidate)));
		}
	}

	async CreateRTCPeerConnection() {
		Logger.debug(this.#LOCAL_INFO + '#CreateRTCPeerConnection');
		if (this.#IsDisposed) {
			Logger.error(this.#LOCAL_INFO + '#CreateRTCPeerConnection', 'Failed to Create RTCPeerConnection: Disposed');
			return;
		}
		if (this.#RTCPeerConnection) {
			Logger.error(this.#LOCAL_INFO, '#CreateRTCPeerConnection', 'Failed to CreateRTCPeerConnection: Already Exists');
			return;
		}
		this.#RTCPeerConnection = new RTCPeerConnection(this.#RTCConfiguration);
		this.#RTCPeerConnection.oniceconnectionstatechange = event => this.#OnIceConnectionStateChange(event);
		this.#RTCPeerConnection.onicegatheringstatechange = event => this.#OnIceGatheringStateChange(event);
		this.#RTCPeerConnection.onnegotiationneeded = event => this.#OnNegotiationNeeded(event);
		this.#RTCPeerConnection.onremovetrack = event => this.#OnRemoveStream(event);
		this.#RTCPeerConnection.onsignalingstatechange = event => this.#OnSignalingStateChange(event);
		this.#RTCPeerConnection.ontrack = event => this.#OnTrack(event);
		this.#RTCPeerConnection.onicecandidate = event => this.#OnIceCandidate(event);

		this.#FetchStatsInterval = setInterval(() => this.#FetchStats(), 4000);
		this.#RTCPeerConnection.addTransceiver('video', { direction: 'recvonly' });
		this.#RTCPeerConnection.addTransceiver('audio', { direction: 'recvonly' });
	}

	dispose() {
		Logger.debug(this.#LOCAL_INFO + 'dispose');
		if (this.#IsDisposed) {
			Logger.error(this.#LOCAL_INFO + 'dispose', 'Failed to Dispose: Already Disposed');
			return;
		}
		clearInterval(this.#FetchStatsInterval);
		if (this.#RTCPeerConnection) {
			this.UpdateSenderStream(null);
			this.#RTCPeerConnection.close();
			this.#RTCPeerConnection = null;
		}
		this.#RemoteStream.getTracks().forEach(track => this.#RemoteStream.removeTrack(track));
		this.#IsDisposed = true;
	}

	GetRemoteStream() {
		if (this.#IsDisposed) {
			Logger.error(this.#LOCAL_INFO + 'GetRemoteStream', 'Failed to Get Remote Stream: Disposed');
			return null;
		}
		if (!this.#RTCPeerConnection) {
			Logger.error(this.#LOCAL_INFO + 'GetRemoteStream', 'Failed to Get Remote Stream: No RTCPeerConnection');
			return null;
		}

		const activeTracks = this.#RemoteStream
			.getTracks()
			.filter(track => !!track && track.readyState !== 'ended');

		Logger.debug(this.#LOCAL_INFO + 'GetRemoteStream', 'Remote Tracks:', activeTracks);
		if (activeTracks.length === 0) {
			return null;
		}
		return {
			PeerId: this.#PeerId,
			UserId: this.#UserId,
			DispName: this.#DispName,
			stream: this.#RemoteStream
		};
	}

	GetContext() {
		return {
			PeerId: this.#PeerId,
			UserId: this.#UserId,
			DispName: this.#DispName,
			Context: this.#Context
		};
	}

	#BuildSenderParameters(existingParameters, preferredParameters) {
		const updatedParameters = {
			...existingParameters
		};

		if (preferredParameters.degradationPreference !== undefined) {
			updatedParameters.degradationPreference = preferredParameters.degradationPreference;
		}

		if (preferredParameters.encodings?.length > 0) {
			const existingEncodings = existingParameters.encodings?.length > 0
				? existingParameters.encodings
				: [{}];

			updatedParameters.encodings = existingEncodings.map((encoding) => ({
				...encoding,
				...preferredParameters.encodings[0]
			}));
		}

		return updatedParameters;
	}

	SetSenderParameters() {
		if (this.#IsDisposed) {
			Logger.error(this.#LOCAL_INFO + 'SetSenderParameters', 'Failed to Set Sender Parameters: Disposed');
			return;
		}
		if (!this.#RTCPeerConnection) {
			Logger.error(this.#LOCAL_INFO + 'SetSenderParameters', 'Failed to Set Sender Parameters: No RTCPeerConnection');
			return;
		}
	}

	#ForceH264(transceiver) {
		Logger.debug(this.#LOCAL_INFO + '#ForceH264', transceiver);
		if (this.#IsDisposed) {
			Logger.error(this.#LOCAL_INFO + '#ForceH264', 'Failed to Force H264: Disposed');
			return;
		}
		if (!transceiver) {
			Logger.error(this.#LOCAL_INFO + '#ForceH264', 'Failed to Force H264: No Transceiver');
			return;
		}
		const supportedCodecs = RTCRtpReceiver.getCapabilities('video')?.codecs ?? [];

		Logger.debug(this.#LOCAL_INFO + '#ForceH264', 'All Supported Codecs: ', supportedCodecs);

		let h264Codecs = supportedCodecs.filter(codec =>
			(codec.mimeType?.toLowerCase() ?? '').includes('video/h264') &&
			codec.sdpFmtpLine?.toLowerCase().includes('level-asymmetry-allowed=1') &&
			codec.sdpFmtpLine?.toLowerCase().includes('packetization-mode=1'));

		Logger.debug(this.#LOCAL_INFO + '#ForceH264', 'Filtered Supported Codecs: ', h264Codecs);

		if (h264Codecs.length > 0) {
			transceiver.setCodecPreferences(h264Codecs);
		}
	}


	#MungeGoogleH264Bitrates(sdp, {
		minKbps = GRTCConstants.DEFAULT_SENDER_HIGH_BANDWIDTH_MIN_BANDWIDTH,
		startKbps = GRTCConstants.DEFAULT_SENDER_HIGH_BANDWIDTH_START_BANDWIDTH,
		maxKbps = GRTCConstants.DEFAULT_SENDER_HIGH_BANDWIDTH_MAX_BANDWIDTH
	} = {}) {
		const googleParams =
			`x-google-min-bitrate=${minKbps};` +
			`x-google-start-bitrate=${startKbps};` +
			`x-google-max-bitrate=${maxKbps}`;

		return sdp.replace(/m=video[\s\S]*?(?=\r?\nm=|$)/g, videoSection => {
			const h264PayloadTypes = [
				...videoSection.matchAll(/^a=rtpmap:(\d+)\s+H264\/90000.*$/gmi)
			].map(match => match[1]);

			for (const payloadType of h264PayloadTypes) {
				const fmtpRegex = new RegExp(`^a=fmtp:${payloadType}\\s+(.+)$`, "mi");

				if (fmtpRegex.test(videoSection)) {
					videoSection = videoSection.replace(fmtpRegex, (_, fmtpBody) => {
						const cleaned = fmtpBody
							.split(';')
							.map(part => part.trim())
							.filter(Boolean)
							.filter(part => !/^x-google-(min|start|max)-bitrate=/i.test(part))
							.join(';');

						return `a=fmtp:${payloadType} ${cleaned};${googleParams}`;
					});
				} else {
					const rtpmapRegex = new RegExp(`^(a=rtpmap:${payloadType}\\s+.*)$`, "mi");

					videoSection = videoSection.replace(
						rtpmapRegex,
						`$1\r\na=fmtp:${payloadType} ${googleParams}`
					);
				}
			}

			return videoSection;
		});
	}

	UpdateCodecParameters() {
		Logger.debug(this.#LOCAL_INFO + 'UpdateCodecParameters');
		if (this.#IsDisposed) {
			Logger.error(this.#LOCAL_INFO + 'UpdateCodecParameters', 'Failed to Update Codec Parameters: Disposed');
			return;
		}
		if (!this.#RTCPeerConnection) {
			Logger.error(this.#LOCAL_INFO + 'UpdateCodecParameters', 'Failed to Update Codec Parameters: No RTCPeerConnection');
			return;
		}
		this.#RTCPeerConnection.getTransceivers().forEach(async transceiver => {
			try {
				//let existingParameters = transceiver.getParameters();
				let kind = transceiver.sender?.track?.kind ?? transceiver.receiver?.track?.kind;
				if (kind === 'video') {
					this.#ForceH264(transceiver);
					if (transceiver.sender?.track) {
						const existingParameters = transceiver.sender.getParameters();
						const updatedParameters = this.#BuildSenderParameters(existingParameters, GRTCConstants.SenderVideoParameters);
						await transceiver.sender.setParameters(updatedParameters);
					}
				}

				if (kind === 'audio' && transceiver.sender?.track) {
					const existingParameters = transceiver.sender.getParameters();
					const updatedParameters = this.#BuildSenderParameters(existingParameters, GRTCConstants.SenderAudioParameters);
					await transceiver.sender.setParameters(updatedParameters);
				}
			} catch (e) {
				Logger.error(this.#LOCAL_INFO + 'UpdateCodecParameters', 'Failed to UpdateCodecParameters: ', e);
			}
		});
	}

	UpdateSenderStream(stream) {
		Logger.debug(this.#LOCAL_INFO + 'UpdateSenderStream', stream);
		if (this.#IsDisposed) {
			Logger.error(this.#LOCAL_INFO + 'UpdateSenderStream', 'Failed to Update Sender Stream: Disposed');
			return;
		}
		if (!this.#RTCPeerConnection) {
			Logger.error(this.#LOCAL_INFO + 'UpdateSenderStream', 'Failed to Update Sender Stream: No RTCPeerConnection');
			return;
		}

		this.#RTCPeerConnection.getSenders().forEach(sender => {
			if (!sender.track) return;
			// Not responsible for stopping it, just to remove
			this.#RTCPeerConnection.removeTrack(sender);
		});
		if (stream) {
			stream.getTracks().forEach(track => {
				this.#RTCPeerConnection.addTrack(track, stream);
			});
		}
		this.UpdateCodecParameters();
	}

	async CreateOffer() {
		Logger.debug(this.#LOCAL_INFO + 'CreateOffer');
		if (this.#IsDisposed) {
			Logger.error(this.#LOCAL_INFO + 'CreateOffer', 'Failed to Create Offer: Disposed');
			return;
		}
		if (!this.#RTCPeerConnection) {
			Logger.error(this.#LOCAL_INFO + 'CreateOffer', 'Failed to Create Offer: No RTCPeerConnection');
			return;
		}
		let localDescription = await this.#RTCPeerConnection.createOffer();
		if (!localDescription) {
			Logger.error(this.#LOCAL_INFO + 'CreateOffer', 'Failed to Create Offer');
			return;
		}
		if (GRTCConstants.IsHighBandwidthModeEnabled && localDescription) {
			localDescription.sdp = this.#MungeGoogleH264Bitrates(localDescription.sdp);
		}
		await this.#RTCPeerConnection.setLocalDescription(localDescription);
		this.#ReduxConnector.dispatch(contextSignal(this.#Context.PeerId, this.#Context.User, this.#PeerId, JSON.stringify(localDescription)));
	}

	async GotOffer(offerMessage) {
		Logger.debug(this.#LOCAL_INFO + 'GotOffer', offerMessage);
		if (this.#IsDisposed) {
			Logger.error(this.#LOCAL_INFO + 'GotOffer', 'Failed to Get Offer: Disposed');
			return;
		}
		if (!this.#RTCPeerConnection) {
			Logger.error(this.#LOCAL_INFO + 'GotOffer', 'Failed to Get Offer: No RTCPeerConnection');
			return;
		}
		let offer = JSON.parse(offerMessage);
		if (!offer) {
			Logger.error(this.#LOCAL_INFO + 'GotOffer', 'Failed to Parse Offer');
			return;
		}
		try {
			await this.#RTCPeerConnection.setRemoteDescription(offer);
			let answer = await this.#RTCPeerConnection.createAnswer();
			if (!answer) {
				Logger.error(this.#LOCAL_INFO + 'GotOffer', 'Failed to Create Answer');
			}
			this.#ReduxConnector.dispatch(contextSignal(this.#Context.PeerId, this.#Context.User, this.#PeerId, JSON.stringify(answer)));
		} catch (e) {
			Logger.error(this.#LOCAL_INFO + 'GotOffer', 'Failed to Create Answer: ', e);
		}
	}

	async GotAnswer(answerMessage) {
		Logger.debug(this.#LOCAL_INFO + 'GotAnswer', answerMessage);
		if (this.#IsDisposed) {
			Logger.error(this.#LOCAL_INFO + 'GotAnswer', 'Failed to Get Answer: Disposed');
			return;
		}
		if (!this.#RTCPeerConnection) {
			Logger.error(this.#LOCAL_INFO + 'GotAnswer', 'Failed to Get Answer: No RTCPeerConnection');
			return;
		}
		let answer = JSON.parse(answerMessage);
		if (!answer) {
			Logger.error(this.#LOCAL_INFO + 'GotAnswer', 'Failed to Parse Answer');
			return;
		}
		try {
			if (this.#RTCPeerConnection.signalingState !== 'have-local-offer') {
				Logger.error(this.#LOCAL_INFO + 'GotAnswer', 'Invalid Signaling State', this.#RTCPeerConnection.signalingState);
				return;
			}
			await this.#RTCPeerConnection.setRemoteDescription(new RTCSessionDescription(answer));
		} catch (e) {
			Logger.error(this.#LOCAL_INFO + 'GotAnswer', 'Failed to Set Remote Description: ', e);
		}
	}

	async GotIceCandidate(candidateMessage) {
		Logger.debug(this.#LOCAL_INFO + 'GotIceCandidate', candidateMessage);
		if (this.#IsDisposed) {
			Logger.error(this.#LOCAL_INFO + 'GotIceCandidate', 'Failed to Get Ice Candidate: Disposed');
			return;
		}
		if (!this.#RTCPeerConnection) {
			Logger.error(this.#LOCAL_INFO + 'GotIceCandidate', 'Failed to Get Ice Candidate: No RTCPeerConnection');
			return;
		}
		let candidate = JSON.parse(candidateMessage);
		if (!candidate || !candidate.candidate) {
			Logger.error(this.#LOCAL_INFO + 'GotIceCandidate', 'Invalid Ice Candidate', candidate);
			return;
		}
		try {
			await this.#RTCPeerConnection.addIceCandidate(new RTCIceCandidate({
				sdpMLineIndex: candidate.label,
				candidate: candidate.candidate
			}));
		} catch (e) {
			Logger.error(this.#LOCAL_INFO + 'GotIceCandidate', 'Failed to Add Ice Candidate: ', e);
		}
	}

	async #FetchStats() {
		if (this.#IsDisposed) {
			Logger.error(this.#LOCAL_INFO + 'FetchStats', 'Failed to Fetch Stats: Disposed');
			return;
		}
		if (!this.#RTCPeerConnection) {
			Logger.error(this.#LOCAL_INFO + 'FetchStats', 'Failed to Fetch Stats: No RTCPeerConnection');
			return;
		}

		try {
			const statsSpecResults = await this.#RTCPeerConnection?.getStats(null) ?? {};
			const statsSpec = {};
			statsSpecResults.forEach(stat => {
				if (!stat.id) {
					return;
				}
				statsSpec[stat.id] = stat;
			});
			const finalStats = {...statsSpec};
			this.#ReduxConnector.dispatch({
				type: 'ON_WEBRTC_STATS',
				payload: finalStats
			});
		} catch (e) {
			Logger.error(this.#LOCAL_INFO + 'FetchStats', 'Failed to Fetch Stats:', e);
		}
	}
}

class GnuralRTCHandler {
	#LOCAL_INFO = GRTCConstants.BASE_INFO + 'GnuralRTC/';
	#Context = new PeerContext(null, null, null, null);
	#GPeerConnections = {};
	#LocalStream = null;
	#ReduxConnector = CreateReduxConnector();

	Constructor() {
	}


	#GetLocalStream() {
		Logger.debug(this.#LOCAL_INFO + '#GetLocalStream');
		return this.#LocalStream ?? LocalMediaHandler.getLocalMediaStream() ?? new MediaStream();
	}

	SetLowBandwidthMode(isLowBandwidthMode) {
		if (isLowBandwidthMode) {
			GRTCConstants.SenderVideoParameters = GRTCConstants.DEFAULT_SENDER_LOW_BANDWIDTH_VIDEO_PARAMETERS;
			GRTCConstants.SenderAudioParameters = GRTCConstants.DEFAULT_SENDER_LOW_BANDWIDTH_AUDIO_PARAMETERS;
		} else {
			GRTCConstants.SenderVideoParameters = GRTCConstants.DEFAULT_SENDER_VIDEO_PARAMETERS;
			GRTCConstants.SenderAudioParameters = GRTCConstants.DEFAULT_SENDER_AUDIO_PARAMETERS;
		}
		GRTCConstants.IsHighBandwidthModeEnabled = false;
	}

	SetHighBandwidthMode(isHighBandwidthMode) {
		if (isHighBandwidthMode) {
			GRTCConstants.SenderVideoParameters = GRTCConstants.DEFAULT_SENDER_HIGH_BANDWIDTH_VIDEO_PARAMETERS;
			GRTCConstants.SenderAudioParameters = GRTCConstants.DEFAULT_SENDER_AUDIO_PARAMETERS;
			GRTCConstants.IsHighBandwidthModeEnabled = true;
		} else {
			GRTCConstants.SenderVideoParameters = GRTCConstants.DEFAULT_SENDER_VIDEO_PARAMETERS;
			GRTCConstants.SenderAudioParameters = GRTCConstants.DEFAULT_SENDER_AUDIO_PARAMETERS;
			GRTCConstants.IsHighBandwidthModeEnabled = false;
		}
	}

	SetLocalStream(stream) {
		Logger.debug(this.#LOCAL_INFO + 'SetLocalStream', stream);
		this.#LocalStream = stream;
		this.UpdateAllSenderStreams();
	}

	GetRemoteStreams() {
		let streams = [];
		Object.values(this.#GPeerConnections).forEach(connection => {
			let remoteStream = connection.GetRemoteStream();
			if (!remoteStream) return;
			streams.push(remoteStream);
		});
		Logger.debug(this.#LOCAL_INFO + 'GetRemoteStreams', 'All Remote Streams:', streams);
		return streams;
	}

	UpdateAllSenderStreams() {
		Logger.debug(this.#LOCAL_INFO + 'UpdateAllSenderStreams');
		const existingContexts = Object.values(this.#GPeerConnections).map(connection => connection?.GetContext());
		existingContexts.forEach(async context => await this.CreateOffer(context));
	}

	async CreatePeerConnection(peer) {
		Logger.debug(this.#LOCAL_INFO + 'CreatePeerConnection', peer);
		if (!peer) return null;

		let tempConnection = this.#GPeerConnections[peer.PeerId];

		if (tempConnection) {
			try {
				tempConnection.dispose();
			} catch (e) {
				Logger.error(this.#LOCAL_INFO + 'CreatePeerConnection', 'Failed to Stop Existing PeerConnection: ', e);
			}
		}

		tempConnection = await GPeerConnection.Create(peer.PeerId, peer.Context, peer.UserId, peer.DispName);
		tempConnection.UpdateSenderStream(this.#GetLocalStream());

		this.#GPeerConnections[peer.PeerId] = tempConnection;
		return tempConnection;
	}

	async DisconnectSelf() {
		Logger.debug(this.#LOCAL_INFO + 'DisconnectSelf');
		let localPeerConnectionIds = Object.keys(this.#GPeerConnections);
		for (const peerId of localPeerConnectionIds) {
			await this.RemovePeerConnection(peerId);
		}
		this.LeaveContext();
	}

	async RemovePeerConnection(peerId) {
		Logger.debug(this.#LOCAL_INFO + 'RemovePeerConnection', peerId);
		if (!peerId) return;
		let tempConnection = this.#GPeerConnections[peerId];
		if (!tempConnection) {
			Logger.error(this.#LOCAL_INFO + 'RemovePeerConnection', 'Failed to Find PeerConnection', peerId);
			return;
		}
		try {
			tempConnection.dispose();
		} catch (e) {
			Logger.error(this.#LOCAL_INFO + 'RemovePeerConnection', 'Failed to Stop Existing PeerConnection: ', e);
		}
		delete this.#GPeerConnections[peerId];
		this.#ReduxConnector.dispatch(disconnectPeer(peerId));
	}

	ContextCreated(context) {
		Logger.debug(this.#LOCAL_INFO + 'ContextCreated', context);
		if (!context) return;
		this.#Context = new PeerContext(context.PeerId, context.Context, context.UserId, context.DispName);
	}

	ChangeContext(context) {
		Logger.debug(this.#LOCAL_INFO + 'ChangeContext', context);
		if (!context) return;
		this.#ReduxConnector.dispatch(changeContext(context));
	}

	async ConnectContext(contexts) {
		Logger.debug(this.#LOCAL_INFO + 'ConnectContext', contexts);
		if (!contexts || contexts.length === 0) return;
		for (const context of contexts) {
			try {
				await this.CreateOffer(context);
			} catch (e) {
				Logger.error(this.#LOCAL_INFO + 'ConnectContext', 'Failed to CreateOffer:', e);
			}
		}
	}

	LeaveContext() {
		Logger.debug(this.#LOCAL_INFO + 'LeaveContext');
		this.#ReduxConnector.dispatch(leaveContext());
	}

	async GotIceCandidate(event) {
		Logger.debug(this.#LOCAL_INFO + 'GotIceCandidate', event);
		let tempConnection = this.#GPeerConnections[event.Sender];
		if (!tempConnection) {
			Logger.error(this.#LOCAL_INFO + 'GotIceCandidate', 'Failed to Find PeerConnection for Ice Candidate', event);
			return;
		}
		await tempConnection.GotIceCandidate(event.Message);
	}

	async GotAnswer(event) {
		Logger.debug(this.#LOCAL_INFO + 'GotAnswer', event);
		let tempConnection = this.#GPeerConnections[event.Sender];
		if (!tempConnection) {
			Logger.error(this.#LOCAL_INFO + 'GotAnswer', 'Failed to Find PeerConnection for Answer', event);
			return;
		}
		await tempConnection.GotAnswer(event.Message);
	}

	async GotOffer(event) {
		Logger.debug(this.#LOCAL_INFO + 'GotOffer', event);
		let tempConnection = this.#GPeerConnections[event.Sender];
		if (!tempConnection) {
			Logger.error(this.#LOCAL_INFO + 'GotOffer', 'Failed to Find PeerConnection for Offer', event);
			return;
		}
		await tempConnection.GotOffer(event.Message);
	}

	async CreateOffer(peer) {
		Logger.debug(this.#LOCAL_INFO + 'CreateOffer', peer);
		if (!peer) return;
		let tempConnection = await this.CreatePeerConnection(peer);
		await tempConnection.CreateOffer();
	}


	async PeerConnectionLost(peer) {
		Logger.debug(this.#LOCAL_INFO + 'PeerConnectionLost', peer);
		if (!peer) return;
		let tempConnection = this.#GPeerConnections[peer.PeerId];
		if (!tempConnection) {
			tempConnection = this.#GPeerConnections[peer.Sender];
			if (!tempConnection)
			{
				Logger.error(this.#LOCAL_INFO + 'PeerConnectionLost', 'Failed to Find PeerConnection', peer, this.#GPeerConnections);
				return;
			}
		}
		try {
			tempConnection.dispose();
		} catch (e) {
			Logger.error(this.#LOCAL_INFO + 'PeerConnectionLost', 'Failed to Stop Existing PeerConnection: ', e);
		}
		delete this.#GPeerConnections[peer.PeerId];
	}
}

const GnuralRTC = {};
GnuralRTC.Instance = new GnuralRTCHandler();

export default GnuralRTC;
